/**
 * Posts frontend errors to the backend so they are emailed to er@adsfo.com
 * (same pipeline as Windows/service error emails).
 *
 * Dedupes identical reports within a short window so Sentry + window handlers
 * + ErrorBoundary do not each send a separate email for one crash.
 */

import store from '../store/index'
import { BACKEND_URL } from '../config/env'

let lastReportKey = ''
let lastReportAt = 0
const DEDUPE_MS = 5000

export type ClientErrorReport = {
  message?: string
  stack?: string
  componentStack?: string
}

function isChunkLoadError(message: string, name?: string): boolean {
  return (
    name === 'ChunkLoadError' ||
    message.includes('Loading chunk') ||
    message.includes('ChunkLoadError')
  )
}

/** Sentry reports error.message; window.onerror prefixes "Uncaught TypeError: ". */
function reportKeyMessage(message: string): string {
  return message.replace(/^Uncaught TypeError:\s*/, '')
}

export function reportClientError(report: ClientErrorReport, errorName?: string): void {
  try {
    const message = report.message || 'Unknown frontend error'
    if (isChunkLoadError(message, errorName)) {
      // Expected after deploys / stale caches — do not spam er@adsfo.com
      return
    }

    const key = `${reportKeyMessage(message)}|${(report.stack || '').slice(0, 200)}`
    const now = Date.now()
    if (key === lastReportKey && now - lastReportAt < DEDUPE_MS) {
      return
    }
    lastReportKey = key
    lastReportAt = now

    const baseUrl =
      (store.getState()?.auth?.url as string | undefined) || BACKEND_URL || ''
    if (!baseUrl) {
      return
    }

    const root = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`

    void fetch(`${root}api/errorReports`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message,
        stack: report.stack || '',
        componentStack: report.componentStack || '',
        path: typeof window !== 'undefined' ? window.location.pathname : '',
        clientName:
          (typeof localStorage !== 'undefined' &&
            localStorage.getItem('sessionCompanyName')) ||
          'Unknown Client',
        user:
          typeof localStorage !== 'undefined'
            ? localStorage.getItem('sessionUserName')
            : null,
        time: new Date().toISOString(),
      }),
    }).catch(() => {
      // Reporting must never throw into the app
    })
  } catch {
    // Reporting must never throw into the app
  }
}
